import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { konsollApi } from '../../api/konsoll';
import type { Fil } from '../../api/filer';
import { Ikon, Resultat, storrelse, Tall } from '../reise/DokumentasjonSide';

/**
 * Dokumentasjonen kunden har lastet opp (T103): filen, det kunden skrev, dokumentleserens tolkning med tall og funn,
 * og en lenke for å laste ned filen. Innholdet valideres ikke (beslutning 0051).
 */
export default function DokumentasjonPanel({ reiseId }: { reiseId: string }) {
  const { t, i18n } = useTranslation();
  const [filer, setFiler] = useState<Fil[] | null>(null);
  const [feil, setFeil] = useState<string | null>(null);

  useEffect(() => {
    konsollApi
      .dokumentasjon(reiseId)
      .then(setFiler)
      .catch((e) => setFeil(e instanceof ApiFeil ? e.kode : 'ukjent'));
  }, [reiseId]);

  if (feil) {
    return (
      <p role="alert" className="melding-feil">
        {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
      </p>
    );
  }
  if (!filer) return <p role="status">{t('konsoll.laster')}</p>;
  if (filer.length === 0) return <p className="text-slate-600">{t('konsoll.dokumentasjon.ingen')}</p>;

  return (
    <ul className="flex flex-col gap-4">
      {filer.map((f) => (
        <li key={f.id} className="kort p-5">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-slate-500">
              <Ikon filnavn={f.filnavn} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-slate-900">{f.filnavn}</p>
              {f.beskrivelse && <p className="text-sm text-slate-600">{f.beskrivelse}</p>}
              <p className="text-xs tabular-nums text-slate-500">
                {storrelse(f.storrelse)} · {new Date(f.opplastet).toLocaleString(i18n.resolvedLanguage)}
                {f.tolkning && <> · {t(`reise.dokumentasjon.type.${f.tolkning.dokumenttype}`, { defaultValue: f.tolkning.dokumenttype })}</>}
                {f.sporsmalId && <> · {t('konsoll.dokumentasjon.tilSporsmal', { sporsmal: f.sporsmalId })}</>}
              </p>
            </div>
            <a href={konsollApi.filAdresse(reiseId, f.id)} download={f.filnavn} className="knapp-sekundaer min-h-[2.25rem] px-3 text-sm">
              <Download aria-hidden="true" className="h-4 w-4" />
              {t('konsoll.dokumentasjon.lastNed')}
            </a>
          </div>

          {f.tolkning ? (
            <div className="ml-8 mt-3">
              <p className="text-slate-700">{f.tolkning.oppsummering}</p>
              {f.tolkning.statistikk && <Tall statistikk={f.tolkning.statistikk} />}
              {f.tolkning.funn.length > 0 && (
                <ul className="mt-3 list-disc pl-5 text-sm text-slate-600">
                  {f.tolkning.funn.map((x, i) => (
                    <li key={i}>{x.tekst}</li>
                  ))}
                </ul>
              )}
              {f.tolkning.kolonner.length > 0 && (
                <p className="mt-3 text-xs text-slate-500">
                  {t('konsoll.dokumentasjon.kolonner')} {f.tolkning.kolonner.map((k) => `${k.kolonne} → ${k.betyr}`).join(' · ')}
                </p>
              )}
              {f.tolkning.personopplysninger && <p className="mt-3 merke bg-amber-50 text-amber-900">{t('konsoll.dokumentasjon.personopplysninger')}</p>}
            </div>
          ) : (
            f.status === 'kontrollert' && <p className="ml-8 mt-3 text-sm text-slate-500">{t('konsoll.dokumentasjon.utenTolkning')}</p>
          )}

          {f.status === 'feilet' && (
            <div className="ml-8 mt-3">
              <Resultat fil={f} />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
